import { useAuthStore } from '@/lib/stores/authStore';
import type { StreamingCDSSData, SessionSummary } from '@/lib/types/consultation';

const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(response: Response, errorPrefix: string): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `${errorPrefix}: ${response.status}`);
  }
  return response.json();
}

export interface ConsultationDraft {
  id: string;
  encounterId: string;
  practitionerId: string;
  patientId: string;
  cdssData: StreamingCDSSData | SessionSummary;
  createdAt: string;
  updatedAt: string;
}

export const draftsApi = {
  /**
   * Save or update a consultation draft
   */
  saveDraft: async (
    encounterId: string,
    practitionerId: string,
    patientId: string,
    cdssData: StreamingCDSSData | SessionSummary
  ): Promise<{
    success: boolean;
    draft: { id: string; encounterId: string; updatedAt: string };
  }> => {
    const response = await fetch(`${API_BASE_URL}/api/drafts`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ encounterId, practitionerId, patientId, cdssData }),
    });

    return handleResponse(response, 'Failed to save draft');
  },

  /**
   * Get a consultation draft by encounterId
   */
  getDraft: async (encounterId: string): Promise<ConsultationDraft | null> => {
    const response = await fetch(`${API_BASE_URL}/api/drafts/${encodeURIComponent(encounterId)}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (response.status === 404) {
      return null;
    }

    const data = await handleResponse<{ draft: ConsultationDraft }>(
      response,
      'Failed to fetch draft'
    );
    return data.draft;
  },

  /**
   * Delete a consultation draft by encounterId
   */
  deleteDraft: async (encounterId: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/drafts/${encodeURIComponent(encounterId)}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok && response.status !== 404) {
      await handleResponse(response, 'Failed to delete draft');
    }
  },
};
